import React, { useMemo } from "react";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FullScreen from "@mui/icons-material/Fullscreen";
import FullScreenExit from "@mui/icons-material/FullscreenExit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ViewWeekOutlined from "@mui/icons-material/ViewWeekOutlined";
import { Box, IconButton, Switch, Toolbar } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { MaterialReactTable, MaterialReactTableProps, type MRT_ColumnDef } from "material-react-table";

import { GroupByMenu } from "..";
import { useReportsActions, useReportsQueriesSelector } from "../../../store";
import { ClientDealTableRow, ReportTableData } from "../../../store/types";
import { SetTableColumnBy } from "../Set-table-column-by/SetTableColumnBy";
import { SetTableColumns } from "../Set-table-columns/SetTableColumns";
import { TableCell } from "./TableCell";

import "./MeasuresTable.styles.scss";

interface MeasuresTableProps {
  isLoading: boolean;
  title?: string;
  showOriginalCurrency?: boolean;
  isDealView?: boolean;
  data: ReportTableData | null;
}

export const tableProps: Partial<MaterialReactTableProps<ClientDealTableRow>> = {
  enablePagination: false,
  enableDensityToggle: false,
  muiTablePaperProps: {
    sx: {
      boxShadow: 0,
      "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
        "&:first-child": {
          overflow: "visible !important"
        }
      }
    }
  },
  muiTableBodyCellProps: { sx: { borderColor: "gray.50" } },
  muiTableHeadRowProps: { sx: { backgroundColor: "white" } },
  muiTableBodyRowProps: { sx: { backgroundColor: "white" } },
  muiTableHeadCellProps: { sx: { borderColor: "gray.50", " .MuiDivider-root": { borderWidth: 1 } } },
  icons: {
    ArrowDownwardIcon: (props: { className: string }) => (
      <div className="measures-table__sort-icons">
        <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
        <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
      </div>
    ),
    DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
  },
  muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),
  muiExpandAllButtonProps: { color: "primary" },
  muiBottomToolbarProps: { sx: { backgroundColor: "background.paper" } }
};

export const MeasuresTable: React.FC<MeasuresTableProps> = ({
  isLoading,
  showOriginalCurrency = true,
  title = "Measures",
  data
}) => {
  const { originalCurrency } = useReportsQueriesSelector();
  const { setQueriesAction } = useReportsActions();

  const mrtColumns = useMemo(() => {
    if (!data) {
      return [];
    }

    const columns: MRT_ColumnDef<ClientDealTableRow>[] = data.columns.map<MRT_ColumnDef<ClientDealTableRow>>((col) => {
      return {
        header: col.title,
        id: col.key,
        accessorKey: col.key,
        sortingFn: "basic",
        accessorFn: (row) => row.valueData[col.key],
        Cell: ({ row, column }) => (
          <TableCell
            id={column.id}
            value={row.original.valueData[col.key]}
            postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
          />
        ),
        AggregatedCell: ({ row, column }) => (
          <TableCell
            id={column.id}
            value={row.original.valueData[col.key]}
            postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
            aggregated
          />
        )
      };
    });

    columns.unshift({
      header: "",
      id: "title",
      accessorKey: "title",
      enableColumnDragging: false,
      enableColumnFilter: false,
      Cell: ({ cell, row }) => (
        <TableCell
          indentation={row.getParentRow()?.original.deal_id ? 2 : 0}
          dealId={row.original.deal_id}
          value={cell.getValue() as string}
          formatDate
        />
      ),
      AggregatedCell: ({ cell, row }) => (
        <TableCell dealId={row.original.deal_id} value={cell.getValue() as string} aggregated formatDate />
      )
    } as MRT_ColumnDef<ClientDealTableRow>);

    columns.unshift({
      header: "",
      id: "pme_warning",
      accessorKey: "pme_warning",
      enableColumnDragging: false,
      enableColumnFilter: false,
      enableResizing: false,
      enableSorting: false,
      enableColumnOrdering: false,
      size: 20,
      muiTableHeadCellProps: {
        sx: {
          padding: "0",
          minWidth: "10px",
          width: "15px",
          borderColor: "#E8E8E8",
          flex: "0 0 auto"
        }
      },
      muiTableBodyCellProps: {
        sx: {
          padding: "0",
          minWidth: "10px",
          width: "15px",
          borderColor: "#E8E8E8",
          flex: "0 0 auto"
        }
      },
      Cell: ({ row }) =>
        Boolean(row.original.pme_warning) && (
          <MaterialTooltip isShow title={row.original.pme_warning} centerText placement="top">
            <InfoOutlinedIcon fontSize="sm" sx={{ fill: "#071F35" }} />
          </MaterialTooltip>
        )
    } as MRT_ColumnDef<ClientDealTableRow>);
    return columns;
  }, [data]);

  return (
    <Box sx={{ width: "100%" }} className="measures-table">
      <MaterialReactTable
        key={(!!data).toString()} // That recalculates the initial state once the data is loaded
        {...tableProps}
        renderTopToolbar={({ table }) => {
          return (
            <Toolbar className="measures-table__toolbar">
              <span className="title">{title}</span>
              <div className="measures-table__toolbar-right">
                <MaterialTooltip isShow title={"Toggle Expand"} placement="top">
                  <IconButton onClick={() => table.setIsFullScreen(!table.getState().isFullScreen)}>
                    {table.getState().isFullScreen ? <FullScreenExit /> : <FullScreen />}
                  </IconButton>
                </MaterialTooltip>
                <GroupByMenu icon={<ViewWeekOutlined />} tooltip="Configure Columns">
                  <SetTableColumns />
                </GroupByMenu>
                {/* <GroupByMenu tooltip="Group Rows" icon={<FormatShapesIcon />}>
                  <SetTableGroupBy isDealView />
                </GroupByMenu>
                <GroupByMenu icon={<ViewStreamOutlinedIcon />} tooltip="Configure Rows">
                  <SetTableReportBy isDealView />
                </GroupByMenu> */}
                <GroupByMenu tooltip="Configure Column By">
                  <SetTableColumnBy />
                </GroupByMenu>
                {showOriginalCurrency && (
                  <div className="input-wrap">
                    <span className="input-wrap__title">Original Currency</span>
                    <Switch
                      checked={originalCurrency}
                      onChange={(e, checked) => {
                        setQueriesAction({ originalCurrency: checked });
                      }}
                    />
                  </div>
                )}
              </div>
            </Toolbar>
          );
        }}
        getRowId={(row) => row.id!}
        columns={mrtColumns}
        filterFromLeafRows
        getSubRows={(row) => row.children}
        data={data?.rows || []}
        state={{ isLoading: !data || isLoading }}
        enableExpanding
        enableColumnOrdering
        enableColumnResizing
        enableColumnActions={false}
        layoutMode="grid"
      />
    </Box>
  );
};
